import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import MessageIcon from '@mui/icons-material/Message';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import api from '../services/api';

// Glassmorphism style
const glassStyle = {
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
};

interface PendingContact {
  id: number;
  name: string;
  channel: string;
  channel_display: string;
  identifier: string;
  avatar_url: string | null;
  integration_name: string | null;
  created_at: string;
  conversations_count: number;
}

interface Lead {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  company: string | null;
}

const PendingContacts = () => {
  const [pendingContacts, setPendingContacts] = useState<PendingContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState<Lead[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState<PendingContact | null>(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form para criar novo Lead
  const [newLeadForm, setNewLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
  });

  useEffect(() => {
    loadPendingContacts();
  }, []);

  const loadPendingContacts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/messaging/contacts/pending-linking/');
      setPendingContacts(response.data.results || []);
    } catch (err) {
      console.error('Erro ao carregar contacts pendentes:', err);
      setError('Erro ao carregar contatos pendentes');
    } finally {
      setLoading(false);
    }
  };

  const searchLeads = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await api.get(`/leads/leads/search-for-linking/?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data.results || []);
    } catch (err) {
      console.error('Erro ao buscar leads:', err);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      searchLeads(searchQuery);
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, searchLeads]);

  const handleOpenLinkModal = (contact: PendingContact) => {
    setSelectedContact(contact);
    setSearchQuery('');
    setSearchResults([]);
    setShowLinkModal(true);
  };

  const handleOpenCreateModal = (contact: PendingContact) => {
    setSelectedContact(contact);
    setNewLeadForm({
      name: contact.name,
      email: '',
      phone: '',
      company: '',
    });
    setShowCreateModal(true);
  };

  const handleLinkToLead = async (leadId: number) => {
    if (!selectedContact) return;

    try {
      setLinking(true);
      setError(null);
      await api.post(`/messaging/contacts/${selectedContact.id}/link-to-lead/`, {
        lead_id: leadId,
      });
      setSuccess(`Contact ${selectedContact.name} vinculado com sucesso!`);
      setShowLinkModal(false);
      loadPendingContacts();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao vincular contact');
    } finally {
      setLinking(false);
    }
  };

  const handleCreateNewLead = async () => {
    if (!selectedContact) return;

    try {
      setLinking(true);
      setError(null);
      await api.post(`/messaging/contacts/${selectedContact.id}/create-new-lead/`, newLeadForm);
      setSuccess(`Lead criado e vinculado com sucesso!`);
      setShowCreateModal(false);
      loadPendingContacts();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao criar lead');
    } finally {
      setLinking(false);
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'whatsapp': return <WhatsAppIcon sx={{ color: '#25D366' }} />;
      case 'instagram': return <InstagramIcon sx={{ color: '#E1306C' }} />;
      case 'email': return <EmailIcon sx={{ color: '#EA4335' }} />;
      default: return <MessageIcon sx={{ color: '#757575' }} />;
    }
  };

  const getChannelColor = (channel: string) => {
    switch (channel) {
      case 'whatsapp': return '#25D366';
      case 'instagram': return '#E1306C';
      case 'email': return '#EA4335';
      default: return '#757575';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)',
      overflow: 'hidden',
      p: 2,
      gap: 2
    }}>
      {/* Header */}
      <Box sx={{
        ...glassStyle,
        borderRadius: 3,
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <WarningIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937' }}>
              Contatos Pendentes de Vinculação
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              {pendingContacts.length} contatos aguardando vinculação manual
            </Typography>
          </Box>
        </Box>

        <Tooltip title="Atualizar">
          <IconButton
            onClick={loadPendingContacts}
            disabled={loading}
            sx={{
              ...glassStyle,
              '&:hover': { background: 'rgba(255,255,255,0.9)' }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      {/* Content */}
      <Box sx={{
        flex: 1,
        ...glassStyle,
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : pendingContacts.length === 0 ? (
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 4
          }}>
            <Box sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(34,197,94,0.2), rgba(22,163,74,0.2))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3
            }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: '#22c55e' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937', mb: 1 }}>
              Tudo em dia!
            </Typography>
            <Typography variant="body1" sx={{ color: '#6b7280' }}>
              Não há contatos pendentes de vinculação
            </Typography>
          </Box>
        ) : (
          <Box sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: 3 }
          }}>
            {pendingContacts.map((contact) => (
              <Box
                key={contact.id}
                sx={{
                  p: 2.5,
                  mb: 2,
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.9)',
                  backdropFilter: 'blur(10px)',
                  border: `2px solid ${getChannelColor(contact.channel)}30`,
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={contact.avatar_url || undefined}
                    sx={{
                      width: 56,
                      height: 56,
                      background: `linear-gradient(135deg, ${getChannelColor(contact.channel)}80, ${getChannelColor(contact.channel)})`
                    }}
                  >
                    {contact.name.charAt(0).toUpperCase()}
                  </Avatar>

                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f2937' }}>
                        {contact.name}
                      </Typography>
                      <Chip
                        icon={getChannelIcon(contact.channel)}
                        label={contact.channel_display}
                        size="small"
                        sx={{
                          height: 24,
                          background: `${getChannelColor(contact.channel)}15`,
                          color: getChannelColor(contact.channel),
                          '& .MuiChip-icon': {
                            color: getChannelColor(contact.channel),
                            fontSize: 16
                          }
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5 }}>
                      {contact.identifier}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, fontSize: 12 }}>
                      <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                        {contact.conversations_count} conversa(s)
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                        Criado em {formatDate(contact.created_at)}
                      </Typography>
                      {contact.integration_name && (
                        <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                          Via {contact.integration_name}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      onClick={() => handleOpenLinkModal(contact)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        borderColor: '#6366f1',
                        color: '#6366f1',
                        '&:hover': {
                          borderColor: '#4f46e5',
                          background: 'rgba(99,102,241,0.05)'
                        }
                      }}
                    >
                      Vincular a Lead
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<PersonAddIcon />}
                      onClick={() => handleOpenCreateModal(contact)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #16a34a, #15803d)'
                        }
                      }}
                    >
                      Criar Novo Lead
                    </Button>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Modal: Vincular a Lead Existente */}
      <Dialog
        open={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, ...glassStyle } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(0,0,0,0.05)', fontWeight: 700 }}>
          Vincular Contact a Lead Existente
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedContact && (
            <Box sx={{ mb: 3, p: 2, borderRadius: 2, background: 'rgba(99,102,241,0.1)' }}>
              <Typography variant="body2" sx={{ color: '#6366f1', fontWeight: 600 }}>
                Vinculando: {selectedContact.name} ({selectedContact.channel_display})
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            label="Buscar Lead"
            placeholder="Digite nome, email, telefone ou empresa..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {searchLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {searchResults.length > 0 && (
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {searchResults.map((lead) => (
                <ListItem
                  key={lead.id}
                  component="div"
                  onClick={() => handleLinkToLead(lead.id)}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      background: 'rgba(99,102,241,0.1)'
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
                      {lead.name.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={lead.name}
                    secondary={
                      <span>
                        {lead.email && <span>{lead.email} • </span>}
                        {lead.phone && <span>{lead.phone} • </span>}
                        {lead.company && <span>{lead.company}</span>}
                      </span>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          {searchQuery.length >= 2 && searchResults.length === 0 && !searchLoading && (
            <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', py: 3 }}>
              Nenhum lead encontrado. Você pode criar um novo lead.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
          <Button onClick={() => setShowLinkModal(false)} disabled={linking}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal: Criar Novo Lead */}
      <Dialog
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, ...glassStyle } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(0,0,0,0.05)', fontWeight: 700 }}>
          Criar Novo Lead
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedContact && (
            <Box sx={{ mb: 3, p: 2, borderRadius: 2, background: 'rgba(34,197,94,0.1)' }}>
              <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 600 }}>
                Criando Lead para: {selectedContact.name} ({selectedContact.channel_display})
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            label="Nome"
            value={newLeadForm.name}
            onChange={(e) => setNewLeadForm({ ...newLeadForm, name: e.target.value })}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={newLeadForm.email}
            onChange={(e) => setNewLeadForm({ ...newLeadForm, email: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Telefone"
            value={newLeadForm.phone}
            onChange={(e) => setNewLeadForm({ ...newLeadForm, phone: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Empresa"
            value={newLeadForm.company}
            onChange={(e) => setNewLeadForm({ ...newLeadForm, company: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
          <Button onClick={() => setShowCreateModal(false)} disabled={linking}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateNewLead}
            disabled={linking || !newLeadForm.name}
            sx={{
              background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              '&:hover': {
                background: 'linear-gradient(135deg, #16a34a, #15803d)'
              }
            }}
          >
            {linking ? <CircularProgress size={20} color="inherit" /> : 'Criar e Vincular'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingContacts;
